import {useSelector} from "react-redux";
import {selectCurrentUser} from "../../features/auth/authSlice";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../../static/css/App.css";
import {
  Container,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../static/css/customerTable.css";
import "../../static/css/productManagement.css";
import { formatUtcDate } from "../../utils/utcDate";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import { showConfirm } from "react-confirm-prompt";
import DynamicBreadcrumb from "../header/breadcrumb";
import customerApi from "../../services/customerapi";
import Loader from "../loader/loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import CustomerViewModal from "./CustomerViewModal";
import CustomerCartModal, { CartButton } from "./CustomerCartModal";
library.add(fas);

const AllCustomers = () => {
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const canRevoke = [1, 2].includes(Number(user?.userRole));
  const [revoking, setRevoking] = useState(null);
  const [sessionMessage, setSessionMessage] = useState('');
  const expireSessions = async id => {
    if (!window.confirm(`Expire all existing login sessions for customer #${id}? They can log in again with a fresh OTP.`)) return;
    setRevoking(id); setSessionMessage('');
    try {
      const r = await customerApi.revokeSessions(id);
      if (r.status !== 'SUCCESS') throw new Error(r.message);
      setSessionMessage(`Customer #${id}: existing sessions expired.`);
    } catch (e) {setSessionMessage(e.response?.data?.message || e.message || 'Unable to expire sessions');}
    finally {setRevoking(null);}
  };
  const [cartCustomer, setCartCustomer] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [totalCustomer, setTotalCustomer] = useState(0);
  const [refreshTable, setRefreshTable] = useState(false);
  const [sortModel, setSortModel] = useState([
    {
      field: "created_at",
      sort: "desc",
    },
  ]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15,
  });
  const [openModal, setOpenModal] = useState(false);
  const [notesText, setNotesText] = useState("Enter Reason");
  const [errors, setErrors] = useState({});
  const [closeNotes, setCloseNotes] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [invModal, setInvModal] = useState(false);
  const [customerStatus, setCustomerStatus] = useState(null);
  const [loaderVisible, setLoaderVisible] = useState(false);

  const pathArray = [
    {
      name: "Customers List",
      url: null,
    },
  ];

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const data = await customerApi.getAllCustomers({
        limit: pageSize,
        page,
        sortModel,
        filterModel,
      });

      const formattedRows = data.data.result.map((cust, index) => ({
        id: cust.id,
        sno: index + 1,
        created_at: formatUtcDate(cust.created_at),
        name: cust.name,
        mobile: cust.mobile,
        email: cust.email,
        status: cust.status,
      }));
      setRows(formattedRows);
      setTotalCustomer(data.data.totalRecords[0].total_records || 0);
    } catch (error) {
      console.error("Failed to fetch Customer:", error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, sortModel, filterModel, refreshTable]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleStatusClick = (customerId, status) => {
    const sureTxt =
      status === 1
        ? "Are you sure to change status to inactive?"
        : "Are you sure to change status to active?";
    const decTxt =
      status === 1
        ? "This will inactivate the customer"
        : "This will activate the customer";
    showConfirm(sureTxt, {
      description: decTxt,
      type: "info",
    }).then((answer) => {
      if (answer) {
        setCustomerId(customerId);
        setCustomerStatus(status);
        if (status !== 1) {
          submitUpdateDetails(customerId, status);
        } else {
          setOpenModal(true);
        }
      }
    });
  };
  const handleLoginClick = async (customerId) => {
    setLoaderVisible(true);

    if (customerId) {
      let params = {
        customerId: customerId,
      };
      let showError = 1;
      const resp = await customerApi.getCustomerLoginToKen(params);
      if (resp) {
        const success = resp.status ?? 'Failed'
        if (success === 'SUCCESS') {
          showError = 0;
          const token = await resp.data.token ?? null;
          if (token) {
            // console.log(token, 'token');
            // Fixing the URL concatenation:
            window.open(`${process.env.REACT_APP_WEBSITE_URL}/admin-login?token=${token}`, '_blank');
          }
        }
      }
      if (showError === 1) {
        showConfirm("Oops!", {
          description: "Process failed, kindly try again later!",
          type: "warning",
          hideCancel: true,
        });
      }
    }
    setLoaderVisible(false);
  }

  const columns = [
    {
      field: "sno",
      headerName: "S NO",
      width: 80,
      sortable: false,
      filterable: false,
    },
    {
      field: "created_at",
      headerName: "Created on (UTC)",
      minWidth: 240,
      flex: 1,
    },
    {
      field: "name",
      headerName: "Customer Name",
      minWidth: 180,
      flex: 1,
    },
    {
      field: "mobile",
      headerName: "Mobile",
      minWidth: 150,
      flex: 0.8,
    },
    {
      field: "email",
      headerName: "Email ID",
      minWidth: 220,
      flex: 1.2,
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      headerAlign: "center",
      align: "center",
      sortable: true,

      renderCell: (params) => {
        const isActive = Number(params.value) === 1;

        return (
          <button
            type="button"
            title={
              isActive
                ? "Click to make customer inactive"
                : "Click to make customer active"
            }
            onClick={() =>
              handleStatusClick(
                params.id,
                Number(params.value)
              )
            }
            className={`customer-status-pill ${isActive
              ? "customer-status-active"
              : "customer-status-inactive"
              }`}
          >
            <span className="customer-status-dot" />

            {isActive ? "Active" : "Inactive"}
          </button>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 270,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,

      renderCell: (params) => (
        <div className="customer-action-buttons">
          <CartButton onClick={() => setCartCustomer(params.id)} />
          <Button
            type="button"
            variant="success"
            className="customer-action-icon"
            title="Login as customer"
            aria-label="Login as customer"
            onClick={() => handleLoginClick(params.id)}
          >
            <FontAwesomeIcon icon="fa-solid fa-arrow-right-to-bracket" />
          </Button>

          <Button
            type="button"
            variant="warning"
            className="customer-action-icon"
            title="View customer addresses"
            aria-label="View customer addresses"
            onClick={() =>
              navigate(
                `/admin/customers/${params.id}/addresses`
              )
            }
          >
            <FontAwesomeIcon icon="fa-solid fa-location-dot" />
          </Button>

          <Button
            type="button"
            variant="primary"
            className="customer-action-icon"
            title="View customer details"
            aria-label="View customer details"
            onClick={() => openViewModel(params.id)}
          >
            <FontAwesomeIcon icon="fa-solid fa-eye" />
          </Button>
          {canRevoke && <Button type="button" variant="outline-danger" className="customer-action-icon" disabled={revoking !== null} title={revoking === params.id ? 'Expiring sessions…' : 'Expire customer sessions'} aria-label="Expire customer sessions" onClick={()=>expireSessions(params.id)}><FontAwesomeIcon icon="fa-solid fa-arrow-right-from-bracket" /></Button>}
        </div>
      ),
    },
  ];

  const openViewModel = (id) => {
    if (id !== null) {
      setCustomerId(id);
      setInvModal(true);
    }
  };

  const hideViewModal = () => {
    setCustomerId(null);
    setInvModal(false);
  };

  const validateCancelForm = () => {
    const newErrors = {};
    if (!closeNotes) newErrors.closeNotes = "Notes is required";
    return newErrors;
  };

  const handleInactivate = async (event) => {
    event.preventDefault();
    const formErrors = validateCancelForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
    } else {
      setErrors({});
      try {
        submitUpdateDetails(customerId, customerStatus, closeNotes);
      } catch (error) {
        setErrors({ common: "Update Failed" });
      }
    }
  };

  const submitUpdateDetails = async (customerId, status, notes = null) => {
    try {
      let sendStatus = status === 1 ? 2 : 1;
      let params = {
        action: "status",
        status: sendStatus,
        customerId: customerId,
        reason: notes,
      };

      const resp = await customerApi.updateCustomerStatus(params);
      if (resp) {
        const message = resp.message || "Customer status updated successfully";
        showConfirm("Success", {
          description: message,
          type: "Success",
          hideCancel: true,
        }).then(() => {
          setRefreshTable(!refreshTable);
          setOpenModal(false);
        });
      } else {
        const message = "Process failed, kindly try again later!";
        showConfirm("Oops!", {
          description: message,
          type: "warning",
          hideCancel: true,
        }).then(() => {
          setRefreshTable(!refreshTable);
          setOpenModal(false);
        });
      }
      handleClose();
    } catch (error) {
      showConfirm("Oops!", {
        description: "Process failed, kindly try again later!",
        type: "warning",
        hideCancel: true,
      });
      handleClose();
    }
  };

  const setReason = (e) => {
    setCloseNotes(e.target.value);
  };

  const handleClose = () => {
    setOpenModal(false);
    setNotesText("Enter Reason");
  };

  return (
    <Container fluid className="pm-page">
      <DynamicBreadcrumb pathArray={pathArray} />
      {sessionMessage && <div role="status" className="alert alert-info">{sessionMessage}</div>}
      <CustomerCartModal customerId={cartCustomer} onHide={() => setCartCustomer(null)} />
      <header className="pm-header"><div><span className="pm-eyebrow">CUSTOMERS</span><h1>Customer Management</h1><p>Manage customer accounts, addresses, cart items and status.</p></div></header>
      <section className="pm-summary"><div><span>Total results</span><strong>{totalCustomer}</strong></div><div><span>Current page</span><strong>{rows.length}</strong></div><div><span>Selected customers</span><strong>{rowSelectionModel.length}</strong></div></section>
      {loaderVisible && <Loader />}
      <Box className="pm-table-card">
        <DataGrid
          rows={rows}
          columns={columns}
          getRowHeight={() => 64}
          rowsPerPageOptions={[5, 10, 20]}
          paginationMode="server"
          rowCount={totalCustomer}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          filterMode="server"
          onSortModelChange={setSortModel}
          onFilterModelChange={setFilterModel}
          autosizeOptions={{
            columns: ["mobile", "customerName", "status"],
            includeOutliers: true,
            includeHeaders: false,
          }}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          onRowSelectionModelChange={(row) => {
            setRowSelectionModel(row);
          }}
          rowSelectionModel={rowSelectionModel}
          sx={{
            borderRadius: "10px",
            overflow: "hidden",

            [`& .${gridClasses.cell}`]: {
              display: "flex",
              alignItems: "center",
              py: 0,
            },

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },

            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fff7f8",
            },
          }}
        />
      </Box>

      {customerId > 0 && (
        <CustomerViewModal
          customerId={customerId}
          invModal={invModal}
          change={hideViewModal}
        />
      )}

      <Modal show={openModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title> Enter Reason</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="cancelForm">
            <Form.Group className="mb-3" controlId="closeNotes">
              <Form.Label>Enter inactivate reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={closeNotes}
                onChange={setReason}
                isInvalid={!!errors.closeNotes}
              />
            </Form.Group>
            <Form.Control.Feedback type="invalid">
              {errors.closeNotes}
            </Form.Control.Feedback>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={handleInactivate}>
            Submit Reason
          </Button>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};


export default AllCustomers;
